import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ApiError, api } from '../lib/api';
import { formatBytes } from '@ukinfo/shared';
import { JOB_RUN_STATUS_LABELS, formatDateTime } from '../lib/format';
import { Badge, Button, Card, CardBody, ErrorBlock, LoadingBlock, SectionTitle, Tabs, useToast } from './ui';

type CheckStatus = 'OK' | 'WARN' | 'ERROR';

interface SystemStatus {
  checks: { key: string; label: string; status: CheckStatus; detail: string }[];
  mail: {
    provider: 'disabled' | 'webhook' | 'smtp';
    from: string;
    server: string | null;
    maxAttempts: number;
    outbox: Record<'PENDING' | 'SENT' | 'FAILED' | 'CANCELLED', number>;
    recent: { id: string; kind: string; status: string; attempts: number; createdAt: string; sentAt: string | null; nextAttemptAt: string | null; lastError: string | null }[];
  };
  jobs: {
    enabled: boolean;
    intervalMinutes: number;
    runs: { id: string; name: string; startedAt: string; finishedAt: string | null; status: string; message: string | null }[];
  };
  logs: {
    auth: { id: string; event: string; createdAt: string }[];
    audit: { id: string; entityType: string; action: string; createdAt: string; actor: string | null }[];
  };
}

const CHECK_TONE: Record<CheckStatus, 'success' | 'warning' | 'danger'> = { OK: 'success', WARN: 'warning', ERROR: 'danger' };
const CHECK_LABEL: Record<CheckStatus, string> = { OK: 'in Ordnung', WARN: 'prüfen', ERROR: 'Fehler' };

const MAIL_KIND_LABELS: Record<string, string> = {
  RESET: 'Passwort / Einladung',
  SPORTS_PERSONAL_LINK: 'Persönlicher Link',
  EMAIL_CHANGED: 'Adresse geändert',
  DIGEST: 'Tägliche Zusammenfassung',
  CALENDAR_CHANGED: 'Kalenderänderung',
};

const MAIL_STATUS: Record<string, { label: string; tone: 'success' | 'warning' | 'danger' | 'neutral' }> = {
  SENT: { label: 'gesendet', tone: 'success' },
  PENDING: { label: 'wartet', tone: 'warning' },
  FAILED: { label: 'fehlgeschlagen', tone: 'danger' },
  CANCELLED: { label: 'verworfen', tone: 'neutral' },
};

const JOB_LABELS: Record<string, string> = {
  'task-reminders': 'Aufgaben-Erinnerungen',
  'sponsorship-reminders': 'Sponsor-Erinnerungen',
  'sports-reminders': 'Sport-Erinnerungen',
  'football-sync': 'Spielplan-Abgleich',
  'google-calendar-sync': 'Kalender-Abgleich',
  cleanup: 'Aufräumen',
  'notification-mails': 'E-Mail-Versand',
};

const AUTH_EVENT_LABELS: Record<string, string> = {
  LOGIN_SUCCESS: 'Anmeldung',
  LOGIN_FAILED: 'Anmeldung fehlgeschlagen',
  LOGIN_LIMITED: 'Anmeldung gesperrt (zu viele Versuche)',
  SESSIONS_REVOKED: 'Überall abgemeldet',
  ACCOUNT_INVITED: 'Zugang eingeladen',
  ACCOUNT_ACTIVATED: 'Zugang aktiviert',
  ACCOUNT_DEACTIVATED: 'Zugang deaktiviert',
  ACCOUNT_EMAIL_CHANGED: 'Anmeldeadresse geändert',
  PASSWORD_CHANGED: 'Passwort geändert',
  RESET_REQUEST: 'Passwort-Link angefordert',
  RESET_COMPLETE: 'Passwort zurückgesetzt',
};

/**
 * Betrieb für Systemverantwortliche: Prüfungen, E-Mail-Versand,
 * Hintergrundaufgaben und Protokolle. Adressen und Mailinhalte zeigt der
 * Server bewusst nicht - nur Zustände und Zeitpunkte.
 */
export function SystemTab(): JSX.Element {
  const [bereich, setBereich] = useState<'pruefungen' | 'mail' | 'jobs' | 'speicher' | 'protokolle'>('pruefungen');
  const status = useQuery({
    queryKey: ['operations-system'],
    queryFn: () => api.get<SystemStatus>('/api/operations/system'),
  });

  if (status.isLoading) return <LoadingBlock />;
  if (status.error || !status.data) return <ErrorBlock error={status.error} />;
  const data = status.data;
  const probleme = data.checks.filter((check) => check.status !== 'OK').length;

  return (
    <div className="space-y-4">
      <Tabs
        tabs={[
          { id: 'pruefungen', label: 'Prüfungen', badge: probleme },
          { id: 'mail', label: 'E-Mail', badge: data.mail.outbox.FAILED },
          { id: 'jobs', label: 'Hintergrundaufgaben' },
          { id: 'speicher', label: 'Speicher' },
          { id: 'protokolle', label: 'Protokolle' },
        ]}
        active={bereich}
        onChange={(id) => setBereich(id as typeof bereich)}
      />
      {bereich === 'pruefungen' && <Pruefungen data={data} onRefresh={() => void status.refetch()} refreshing={status.isFetching} />}
      {bereich === 'mail' && <MailBereich data={data} />}
      {bereich === 'jobs' && <JobsBereich data={data} />}
      {bereich === 'speicher' && <SpeicherBereich />}
      {bereich === 'protokolle' && <Protokolle data={data} />}
    </div>
  );
}

function Pruefungen({ data, onRefresh, refreshing }: { data: SystemStatus; onRefresh: () => void; refreshing: boolean }): JSX.Element {
  return (
    <Card>
      <CardBody>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <SectionTitle title="Prüfungen" subtitle="Läuft alles, was im Hintergrund laufen soll?" />
          <Button size="sm" variant="secondary" loading={refreshing} onClick={onRefresh}>Neu prüfen</Button>
        </div>
        <ul className="divide-y divide-slate-100 text-sm">
          {data.checks.map((check) => (
            <li key={check.key} className="flex items-start justify-between gap-3 py-2">
              <span className="min-w-0">
                <span className="block font-medium text-slate-800">{check.label}</span>
                <span className="block break-words text-xs text-slate-500">{check.detail}</span>
              </span>
              <Badge tone={CHECK_TONE[check.status]}>{CHECK_LABEL[check.status]}</Badge>
            </li>
          ))}
        </ul>
      </CardBody>
    </Card>
  );
}

function MailBereich({ data }: { data: SystemStatus }): JSX.Element {
  const toast = useToast();
  const qc = useQueryClient();
  const aktiv = data.mail.provider !== 'disabled';

  const testen = useMutation({
    mutationFn: () => api.post<{ ok: boolean; to: string }>('/api/operations/mail/test'),
    onSuccess: (result) => toast.show(`Testnachricht an ${result.to} angenommen`),
    onError: (error: unknown) => toast.show(error instanceof ApiError ? error.message : 'Testnachricht fehlgeschlagen', 'error'),
  });
  const senden = useMutation({
    mutationFn: () => api.post<{ message: string }>('/api/operations/mail/deliver'),
    onSuccess: async (result) => {
      await qc.invalidateQueries({ queryKey: ['operations-system'] });
      toast.show(result.message);
    },
    onError: (error: unknown) => toast.show(error instanceof ApiError ? error.message : 'Versand fehlgeschlagen', 'error'),
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardBody>
          <SectionTitle title="E-Mail-Versand" subtitle="Einrichtung, Warteschlange und Test" />
          <dl className="grid gap-x-4 gap-y-1 text-sm sm:grid-cols-[auto_1fr]">
            <dt className="text-slate-500">Versandweg</dt>
            <dd className="text-slate-800">{aktiv ? (data.mail.provider === 'smtp' ? `SMTP ${data.mail.server ?? ''}` : 'Webhook') : 'nicht eingerichtet'}</dd>
            <dt className="text-slate-500">Absender</dt>
            <dd className="break-all text-slate-800">{data.mail.from || '–'}</dd>
            <dt className="text-slate-500">Versand</dt>
            <dd className="text-slate-800">
              {data.jobs.enabled ? `alle ${data.jobs.intervalMinutes} Minuten, bis zu ${data.mail.maxAttempts} Versuche` : 'Hintergrundjobs abgeschaltet'}
            </dd>
          </dl>
          <div className="mt-3 flex flex-wrap gap-2 text-sm">
            <Badge tone="warning">{data.mail.outbox.PENDING} wartend</Badge>
            <Badge tone="success">{data.mail.outbox.SENT} gesendet</Badge>
            <Badge tone="danger">{data.mail.outbox.FAILED} fehlgeschlagen</Badge>
            <Badge tone="neutral">{data.mail.outbox.CANCELLED} verworfen</Badge>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button size="sm" disabled={!aktiv} loading={senden.isPending} onClick={() => senden.mutate()}>
              Warteschlange jetzt senden
            </Button>
            <Button size="sm" variant="secondary" disabled={!aktiv} loading={testen.isPending} onClick={() => testen.mutate()}>
              Testnachricht an mich
            </Button>
          </div>
          <p className="hint mt-2">
            Die Zugangsdaten werden auf dem Server über den Workflow „E-Mail-Versand einrichten“ gesetzt, nicht hier.
          </p>
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <SectionTitle title="Letzte E-Mails" subtitle="Ohne Empfänger und Inhalt" />
          {data.mail.recent.length === 0 ? (
            <p className="text-sm text-slate-500">Noch keine E-Mails.</p>
          ) : (
            <ul className="divide-y divide-slate-100 text-sm">
              {data.mail.recent.map((mail) => {
                const status = MAIL_STATUS[mail.status] ?? { label: mail.status, tone: 'neutral' as const };
                return (
                  <li key={mail.id} className="flex items-start justify-between gap-3 py-2">
                    <span className="min-w-0">
                      <span className="block font-medium text-slate-800">{MAIL_KIND_LABELS[mail.kind] ?? mail.kind}</span>
                      <span className="block text-xs text-slate-500">
                        erstellt {formatDateTime(mail.createdAt)}
                        {mail.sentAt && ` · gesendet ${formatDateTime(mail.sentAt)}`}
                        {mail.nextAttemptAt && ` · nächster Versuch ${formatDateTime(mail.nextAttemptAt)}`}
                        {mail.attempts > 1 && ` · ${mail.attempts} Versuche`}
                      </span>
                      {mail.lastError && <span className="block text-xs text-rose-700">{mail.lastError}</span>}
                    </span>
                    <Badge tone={status.tone}>{status.label}</Badge>
                  </li>
                );
              })}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function JobsBereich({ data }: { data: SystemStatus }): JSX.Element {
  const toast = useToast();
  const qc = useQueryClient();
  const ausfuehren = useMutation({
    mutationFn: () => api.post<{ name: string; status: string; message: string }[]>('/api/jobs/run'),
    onSuccess: async (results) => {
      await qc.invalidateQueries({ queryKey: ['operations-system'] });
      await qc.invalidateQueries({ queryKey: ['dashboard'] });
      const failed = results.filter((r) => r.status === 'ERROR').length;
      toast.show(
        failed === 0 ? `${results.length} Hintergrundaufgaben ausgeführt` : `${failed} von ${results.length} Aufgaben fehlgeschlagen`,
        failed === 0 ? 'success' : 'error',
      );
    },
    onError: (err: unknown) => toast.show(err instanceof ApiError ? err.message : 'Ausführung fehlgeschlagen', 'error'),
  });

  return (
    <Card>
      <CardBody>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <SectionTitle
            title="Hintergrundaufgaben"
            subtitle={data.jobs.enabled ? `Erinnerungen, Abgleich, Aufräumen und E-Mail – alle ${data.jobs.intervalMinutes} Minuten` : 'Abgeschaltet'}
          />
          <Button size="sm" onClick={() => ausfuehren.mutate()} loading={ausfuehren.isPending}>Jetzt ausführen</Button>
        </div>
        {data.jobs.runs.length === 0 ? (
          <p className="text-sm text-slate-500">Noch keine Läufe protokolliert.</p>
        ) : (
          <ul className="divide-y divide-slate-100 text-sm">
            {data.jobs.runs.map((run) => (
              <li key={run.id} className="flex items-start justify-between gap-3 py-2">
                <span className="min-w-0">
                  <span className="block font-medium text-slate-800">{JOB_LABELS[run.name] ?? run.name}</span>
                  {run.message && <span className="block break-words text-xs text-slate-500">{run.message}</span>}
                </span>
                <span className="flex shrink-0 flex-col items-end">
                  <Badge tone={run.status === 'OK' ? 'success' : run.status === 'RUNNING' ? 'neutral' : 'danger'}>{JOB_RUN_STATUS_LABELS[run.status] ?? run.status}</Badge>
                  <span className="text-xs text-slate-400">{formatDateTime(run.startedAt)}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

function Protokolle({ data }: { data: SystemStatus }): JSX.Element {
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardBody>
          <SectionTitle title="Anmeldungen und Zugänge" subtitle="Ohne Adressen – nur Ereignis und Zeitpunkt" />
          {data.logs.auth.length === 0 ? (
            <p className="text-sm text-slate-500">Keine Einträge.</p>
          ) : (
            <ul className="divide-y divide-slate-100 text-sm">
              {data.logs.auth.map((entry) => (
                <li key={entry.id} className="flex items-center justify-between gap-3 py-1.5">
                  <span className={entry.event === 'LOGIN_FAILED' || entry.event === 'LOGIN_LIMITED' ? 'text-rose-700' : 'text-slate-800'}>
                    {AUTH_EVENT_LABELS[entry.event] ?? entry.event}
                  </span>
                  <span className="shrink-0 text-xs text-slate-400">{formatDateTime(entry.createdAt)}</span>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
      <Card>
        <CardBody>
          <SectionTitle title="Änderungsprotokoll" subtitle="Wer hat was geändert" />
          {data.logs.audit.length === 0 ? (
            <p className="text-sm text-slate-500">Keine Einträge.</p>
          ) : (
            <ul className="divide-y divide-slate-100 text-sm">
              {data.logs.audit.map((entry) => (
                <li key={entry.id} className="flex items-start justify-between gap-3 py-1.5">
                  <span className="min-w-0">
                    <span className="block text-slate-800">{entry.entityType} · {entry.action}</span>
                    <span className="block truncate text-xs text-slate-500">{entry.actor ?? 'System'}</span>
                  </span>
                  <span className="shrink-0 text-xs text-slate-400">{formatDateTime(entry.createdAt)}</span>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

interface StorageStatus {
  videos: { usedBytes: number; videoCount: number; quotaBytes: number };
  disk: { freeBytes: number; totalBytes: number } | null;
  backups: { enabled: boolean; keep: number; count: number; bytes: number; newestAt: string | null };
}

/** Anteil in Prozent, auf ganze Zahlen begrenzt (0 bis 100). */
const percent = (part: number, whole: number): number => (whole > 0 ? Math.max(0, Math.min(100, Math.round((part / whole) * 100))) : 0);

function Balken({ wert, label }: { wert: number; label: string }): JSX.Element {
  return <progress className="mt-1 h-2 w-full" max={100} value={wert} aria-label={label} />;
}

/**
 * Speicher: wie voll sind Trainingsvideos, Datentraeger und Sicherungen? Nur lesend - Grenzen fuer Videos
 * stehen unter "Verein", Aufbewahrung der Sicherungen ist Serverkonfiguration (BACKUP_KEEP).
 */
function SpeicherBereich(): JSX.Element {
  const status = useQuery({
    queryKey: ['operations-storage'],
    queryFn: () => api.get<StorageStatus>('/api/operations/storage'),
  });
  if (status.isLoading) return <LoadingBlock />;
  if (status.error || !status.data) return <ErrorBlock error={status.error} />;
  const { videos, disk, backups } = status.data;
  const videoShare = percent(videos.usedBytes, videos.quotaBytes);
  const diskUsed = disk ? disk.totalBytes - disk.freeBytes : 0;
  const diskShare = disk ? percent(diskUsed, disk.totalBytes) : 0;
  const diskTone: 'success' | 'warning' | 'danger' = !disk ? 'warning' : disk.freeBytes < disk.totalBytes * 0.05 ? 'danger' : disk.freeBytes < disk.totalBytes * 0.15 ? 'warning' : 'success';

  return (
    <div className="space-y-4">
      <Card>
        <CardBody>
          <div className="flex flex-wrap items-start justify-between gap-2">
            <SectionTitle title="Trainingsvideos" subtitle="Belegter Platz aller Videos dieses Vereins" />
            <Button size="sm" variant="secondary" loading={status.isFetching} onClick={() => void status.refetch()}>Neu laden</Button>
          </div>
          <p className="text-sm text-slate-800">
            {formatBytes(videos.usedBytes)} von {formatBytes(videos.quotaBytes)} belegt · {videos.videoCount} {videos.videoCount === 1 ? 'Video' : 'Videos'}
          </p>
          <Balken wert={videoShare} label="Belegung des Videokontingents" />
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <SectionTitle title="Datenträger des Servers" subtitle="Datenverzeichnis mit Datenbank, Dateien und Sicherungen" />
          {disk ? (
            <>
              <p className="flex flex-wrap items-center gap-2 text-sm text-slate-800">
                Frei: {formatBytes(disk.freeBytes)} von {formatBytes(disk.totalBytes)}
                <Badge tone={diskTone}>{diskTone === 'success' ? 'in Ordnung' : diskTone === 'warning' ? 'wird knapp' : 'fast voll'}</Badge>
              </p>
              <Balken wert={diskShare} label="Belegung des Datenträgers" />
            </>
          ) : (
            <p className="text-sm text-slate-500">Der Server nennt den freien Platz nicht.</p>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <SectionTitle title="Sicherungen" subtitle="Unveränderte Dateien werden nicht erneut abgelegt; es bleiben die letzten Sicherungen erhalten" />
          {backups.enabled || backups.count > 0 ? (
            <ul className="space-y-1 text-sm text-slate-800">
              <li>{backups.count} {backups.count === 1 ? 'Sicherung' : 'Sicherungen'} vorhanden (Aufbewahrung: die letzten {backups.keep})</li>
              <li>Belegter Platz: {formatBytes(backups.bytes)}</li>
              <li>Letzte Sicherung: {backups.newestAt ? formatDateTime(backups.newestAt) : 'keine gefunden'}</li>
            </ul>
          ) : (
            <p className="text-sm text-slate-500">Sicherungen sind abgeschaltet (BACKUP_ENABLED).</p>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
